import React, { useState, useMemo } from 'react';
import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectNotes({ project, updateProject }) {
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [newNote, setNewNote] = useState({ 
    title: '', 
    content: '', 
    category: 'General',
    isPinned: false 
  });

  const categories = ['General', 'Meeting', 'Vendor', 'Logistics', 'Budget', 'Design'];
  const notes = project.notes || [];

  // Robust filtering and sorting: Pinned first, then by date descending
  const filteredNotes = useMemo(() => {
    let result = [...notes];
    
    if (activeCategory !== 'All') {
      result = result.filter(n => n.category === activeCategory);
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(n => 
        n.title.toLowerCase().includes(q) || 
        n.content.toLowerCase().includes(q)
      );
    }
    
    return result.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.date || 0) - new Date(a.date || 0);
    });
  }, [notes, searchQuery, activeCategory]);

  const addNote = (e) => {
    e.preventDefault();
    if (!newNote.title || !newNote.content) return;
    
    const noteToSave = { 
      id: Date.now().toString(), 
      ...newNote, 
      date: new Date().toISOString() 
    };

    updateProject(project.id, { 
      notes: [noteToSave, ...notes] 
    });
    
    setNewNote({ title: '', content: '', category: 'General', isPinned: false });
    setIsAdding(false);
  };

  const togglePin = (id) => {
    const updatedNotes = notes.map(n => 
      n.id === id ? { ...n, isPinned: !n.isPinned } : n
    );
    updateProject(project.id, { notes: updatedNotes });
  };

  const removeNote = (id) => {
    if (window.confirm("Delete this note?")) {
      updateProject(project.id, { notes: notes.filter(n => n.id !== id) });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold font-serif text-gray-900">Project Notes</h2>
          <p className="text-sm text-gray-500 mt-1">Keep track of meetings, decisions, and important details.</p>
        </div>
        <button 
          onClick={() => setIsAdding(!isAdding)} 
          className={`px-6 py-2.5 rounded-xl font-bold transition-all shadow-md flex items-center gap-2 ${isAdding ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-pink-600 text-white hover:bg-pink-700'}`}
        >
          {isAdding ? 'Cancel' : <><PlannerIcons.Plus /> Create Note</>}
        </button>
      </div>

      {/* Robust Add Note Form */}
      {isAdding && (
        <div className="bg-white p-6 rounded-2xl border-2 border-pink-100 shadow-lg animate-fade-in-up relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-pink-500"></div>
          <form onSubmit={addNote} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex-grow">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Note Title</label>
                <input 
                  type="text" 
                  placeholder="e.g. Venue Walkthrough Dec 12" 
                  value={newNote.title} 
                  onChange={e => setNewNote({ ...newNote, title: e.target.value })} 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-gray-900 focus:ring-2 focus:ring-pink-500 outline-none transition" 
                  required 
                />
              </div>
              <div className="w-full md:w-48 shrink-0">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Category</label>
                <select 
                  value={newNote.category} 
                  onChange={e => setNewNote({ ...newNote, category: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-pink-500 outline-none transition appearance-none"
                >
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">Content</label>
              <textarea 
                placeholder="Write your notes here..." 
                value={newNote.content} 
                onChange={e => setNewNote({ ...newNote, content: e.target.value })} 
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl h-40 resize-none text-sm font-medium focus:ring-2 focus:ring-pink-500 outline-none transition" 
                required 
              />
            </div>
            
            <div className="flex justify-between items-center pt-2">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input 
                  type="checkbox" 
                  checked={newNote.isPinned} 
                  onChange={e => setNewNote({ ...newNote, isPinned: e.target.checked })}
                  className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500 border-gray-300"
                />
                <span className="text-xs font-bold text-gray-500 group-hover:text-gray-900 transition-colors uppercase tracking-widest">Pin to top</span>
              </label>
              <button type="submit" className="bg-gray-900 text-white px-8 py-3 rounded-xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95">
                Save Project Note
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters & Search Toolbar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 flex bg-white p-2 rounded-2xl border border-gray-200 shadow-sm">
            <div className="pl-3 pr-2 py-2 text-gray-400">
                <PlannerIcons.Search />
            </div>
            <input 
                type="text" 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search notes..." 
                className="w-full bg-transparent border-none focus:ring-0 text-sm font-medium text-gray-700"
            />
        </div>
        <div className="flex gap-2 overflow-x-auto custom-scrollbar no-scrollbar-mobile pb-2 md:pb-0">
          {['All', ...categories].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all border whitespace-nowrap ${activeCategory === cat ? 'bg-gray-900 text-white border-gray-900 shadow-md' : 'bg-white text-gray-500 border-gray-200 hover:border-pink-300'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Notes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredNotes.length > 0 ? filteredNotes.map(note => (
          <div 
            key={note.id} 
            className={`bg-[#fffef7] p-6 rounded-3xl border transition-all duration-300 relative group flex flex-col min-h-[220px] ${note.isPinned ? 'border-amber-200 shadow-md ring-1 ring-amber-100' : 'border-gray-200 hover:shadow-lg'}`}
          >
            {note.isPinned && (
              <div className="absolute -top-2 -left-2 bg-amber-400 text-white p-1.5 rounded-lg shadow-md z-10">
                <PlannerIcons.Pin />
              </div>
            )}
            
            <div className="flex justify-between items-start mb-4">
              <div className="min-w-0 pr-6">
                <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-tighter mb-1 inline-block border border-amber-100">
                  {note.category || 'General'}
                </span>
                <h4 className="font-serif text-xl font-bold text-gray-900 truncate leading-tight">{note.title}</h4>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-4 right-4">
                <button 
                  onClick={() => togglePin(note.id)} 
                  className={`p-2 rounded-full transition-colors ${note.isPinned ? 'text-amber-500 hover:text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-gray-600 bg-white shadow-sm'}`}
                  title={note.isPinned ? "Unpin Note" : "Pin Note"}
                >
                  <PlannerIcons.Pin />
                </button>
                <button 
                  onClick={() => removeNote(note.id)} 
                  className="p-2 text-gray-400 hover:text-red-500 bg-white rounded-full shadow-sm transition-colors"
                  title="Delete Note"
                >
                  <PlannerIcons.Trash />
                </button>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 font-medium line-clamp-6 leading-relaxed mb-6 whitespace-pre-wrap flex-grow">
              {note.content}
            </p>
            
            <div className="mt-auto pt-4 border-t border-amber-100/50 flex justify-between items-center">
              <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                {new Date(note.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              {note.author && (
                <span className="text-[10px] bg-white border border-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-bold italic">
                  — {note.author}
                </span>
              )}
            </div>
          </div>
        )) : (
          <div className="col-span-full py-24 text-center border-2 border-dashed border-gray-200 rounded-[3rem] bg-gray-50/50">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm text-4xl grayscale opacity-50">📝</div>
            <h4 className="font-serif text-xl font-bold text-gray-900">No notes found.</h4>
            <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
              {searchQuery || activeCategory !== 'All' 
                ? "Try adjusting your filters or search terms." 
                : "Create your first project note to keep your planning organized."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}